<script setup>
import { computed } from "vue";
import { parseInline } from "@/utils/markdown";
import { parseSlides } from "@/utils/slides";
import InlineRuns from "./InlineRuns.vue";

// A slides make shown as the deck it downloads as: a title slide, then one per section
const props = defineProps({
  text: { type: String, default: "" },
  fillable: { type: Boolean, default: false },
  blankValues: { type: Object, default: () => ({}) },
});
defineEmits(["blank"]);

const deck = computed(() => parseSlides(props.text));
</script>

<template>
  <div class="space-y-4 text-primary [overflow-wrap:anywhere]">
    <section
      class="flex aspect-video w-full flex-col justify-center rounded-lg bg-primary p-6 shadow sm:p-10"
      aria-label="Title slide"
    >
      <h3 class="text-2xl font-bold text-white sm:text-3xl">
        <InlineRuns
          :runs="parseInline(deck.title)"
          :fillable="fillable"
          :blankValues="blankValues"
          @blank="$emit('blank', $event)"
        />
      </h3>
      <div class="my-3 h-1 w-12 rounded bg-accentPrimary"></div>
      <p v-if="deck.subtitle" class="text-base text-accentSecondaryLight">
        <InlineRuns
          :runs="parseInline(deck.subtitle)"
          :fillable="fillable"
          :blankValues="blankValues"
          @blank="$emit('blank', $event)"
        />
      </p>
    </section>
    <section
      v-for="(slide, index) in deck.slides"
      :key="index"
      class="rounded-lg border border-gray-200 bg-white shadow-sm"
      :aria-label="`Slide ${index + 2}`"
    >
      <div class="flex aspect-video w-full flex-col p-5 sm:p-8">
        <p class="mb-1 text-xs font-bold text-secondaryLight">
          {{ index + 2 }}
        </p>
        <h4 class="text-lg font-bold sm:text-xl">
          <InlineRuns
            :runs="
              parseInline(slide.title).map((run) => ({ ...run, bold: false }))
            "
            :fillable="fillable"
            :blankValues="blankValues"
            @blank="$emit('blank', $event)"
          />
        </h4>
        <div class="my-2 h-1 w-10 rounded bg-accentPrimary"></div>
        <ul class="list-disc space-y-1 pl-5 text-sm sm:text-base">
          <li v-for="(bullet, i) in slide.bullets" :key="i">
            <InlineRuns
              :runs="parseInline(bullet)"
              :fillable="fillable"
              :blankValues="blankValues"
              @blank="$emit('blank', $event)"
            />
          </li>
        </ul>
      </div>
      <p
        v-if="slide.notes"
        class="border-t border-gray-200 bg-gray-10 px-5 py-2 text-sm text-secondary sm:px-8"
      >
        <span class="font-bold">Notes:</span> {{ slide.notes }}
      </p>
    </section>
  </div>
</template>
